import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import Image from 'next/image';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { aspectRatio } from 'utils/styles.utils';

import { useAccount } from 'queries/accounts/useAccount';

import AppStoresWithQR from 'components/@common/AppStoresWithQR';
import BorderStack from 'components/@common/BorderStack';
import Icon from 'components/@icons/Icon';
import { isPrivateRelayEmail } from 'components/@transfer/InvalidTransfer/util';

interface Props {
  borderColor?: 'background.default' | 'global01.100';
  sx?: SxProps<Theme>;
}

const DownloadApp = ({ borderColor = 'background.default', sx }: Props) => {
  const { account } = useAccount();

  return (
    <BorderStack
      sx={[
        {
          borderColor,
          p: 0,
          overflow: 'hidden',
          bgcolor: 'global01.800',
          position: 'relative',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'space-between',
          px: 2,
          py: 1.75,
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor,
        }}
      >
        <Icon name="Info" />
        <Stack
          sx={{
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
            }}
          >
            <FormattedMessage id="download_app.info.title" />
          </Typography>
          <Stack>
            <Typography variant="small">
              <FormattedMessage
                id="download_app.info.description"
                values={{
                  b: (chunks: Array<ReactNode>) => {
                    return (
                      <Typography
                        variant="small"
                        component="span"
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        {chunks}
                      </Typography>
                    );
                  },
                  email: account?.email,
                }}
              />
            </Typography>
            {isPrivateRelayEmail(account?.email) && (
              <Typography variant="small">
                <FormattedMessage id="transfer_tickets.invalid.details.apple_account" />
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'center', md: 'flex-start' },
          flex: '1',
          flexDirection: 'column',
          p: 2,
          gap: 2,
        }}
      >
        <AppStoresWithQR />
      </Box>
      <Box
        sx={[
          {
            position: 'absolute',
            bottom: '0',
            display: { xs: 'none', md: 'block' },
            width: '185px',
            height: 'auto',
            alignSelf: 'flex-end',
          },
          ({ direction }) => ({
            ...aspectRatio(555, 474),
            transform: `rotateY(${direction === 'rtl' ? '-180deg' : '0deg'})`,
          }),
        ]}
      >
        <Image
          src="/images/download-app-background.webp"
          fill
          style={{ objectFit: 'contain' }}
          sizes="256px"
          alt="Download App Background"
          priority
          unoptimized
        />
      </Box>
    </BorderStack>
  );
};

export default DownloadApp;
